/*
 * BIGVITA (2026-10): product price + Appstle subscription widget customizations.
 *
 * Loaded on every page (layout/theme.liquid via snippets/appstle-customizations.liquid),
 * so it also works in the quick-add drawer, whose HTML is copied from the product page
 * (inline scripts do not run there).
 *
 * Each product area ("scope") is handled on its own:
 *   - the quick-add drawer (.wt__quick-buy--wrapper), or
 *   - the Shopify section that contains the product (product page, featured product).
 * Per-variant data comes from <script type="application/json" data-bv-product> rendered by
 * snippets/bigvita-product-data.liquid inside the price block.
 *
 * Rules (always from real Shopify prices):
 *   - Reference (strikethrough, black) price = variant compare-at price, or the one-time
 *     price when a selling plan is selected and there is no higher compare-at.
 *   - Current (red when discounted) price = selected selling plan price, or the one-time price.
 *   - SAVE amount / Save % are measured against the reference price.
 *   - Per-day cost = current price / round(months x 30.4), rounded to the nearest cent.
 */
(function () {
  'use strict';
  if (window.__bvPricingLoaded) return;
  window.__bvPricingLoaded = true;

  var CONFIG = window.BIGVITA || {};
  var MONEY_FORMAT = CONFIG.moneyFormat || '${{amount}}';
  var DEFAULT_ONETIME_HTML = '<ul class="appstle-bullets"><li class="appstle-onetime"><strong>No commitment</strong> &middot; Ships once</li></ul>';
  var ONE_TIME_SEL = '[class*="one_time"], [class*="onetime"], [class*="one-time"], [class*="oneTime"]';
  var MONEY_RE = /[$€£]?\s?\d[\d,]*(?:\.\d{1,2})?/;
  var PRICE_SETTLE_MS = 150;
  var PRICE_FALLBACK_MS = 1200;
  var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
  var dataCache = typeof WeakMap === 'function' ? new WeakMap() : null;

  // ---------- money ----------
  function withCommas(numStr) {
    var parts = String(numStr).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var value = cents / 100;
    return MONEY_FORMAT
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas(value.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(Math.round(value)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas(value.toFixed(2)).replace(/\./g, ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas(Math.round(value)));
  }
  function parseCents(text) {
    if (!text) return 0;
    var cleaned = text.replace(/[^0-9.,]/g, '').replace(/,(\d{2})$/, '.$1').replace(/,/g, '');
    var n = parseFloat(cleaned);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  // ---------- scopes ----------
  function scopeOf(el) {
    if (!el || !el.closest) return null;
    return el.closest('.wt__quick-buy--wrapper') || el.closest('.shopify-section') || el.closest('.wt-product') || null;
  }
  function readData(el) {
    if (dataCache && dataCache.has(el)) return dataCache.get(el);
    var data = null;
    try { data = JSON.parse(el.textContent); } catch (e) { data = null; }
    if (dataCache) dataCache.set(el, data);
    return data;
  }
  // Build a context object for one product area, or null when it has no BIGVITA data.
  function getContext(scope) {
    if (!scope || !scope.querySelector) return null;
    var dataEl = scope.querySelector('script[data-bv-product]');
    if (!dataEl) return null;
    var data = readData(dataEl);
    if (!data || !data.variants) return null;
    var ctx = { scope: scope, data: data };
    ctx.variantId = getSelectedVariantId(ctx);
    ctx.variant = data.variants[ctx.variantId] || null;
    ctx.widget = scope.querySelector('.appstle_sub_widget');
    return ctx;
  }
  function allContexts() {
    var seen = [];
    var out = [];
    document.querySelectorAll('script[data-bv-product]').forEach(function (el) {
      var scope = scopeOf(el) || document;
      if (seen.indexOf(scope) !== -1) return;
      seen.push(scope);
      var ctx = getContext(scope);
      if (ctx) out.push(ctx);
    });
    return out;
  }

  // ---------- selection ----------
  function getSelectedVariantId(ctx) {
    var scope = ctx.scope;
    var variants = ctx.data.variants;
    var candidates = [];
    scope.querySelectorAll('variant-options[data-variant-id]').forEach(function (vo) {
      candidates.push(vo.getAttribute('data-variant-id'));
    });
    scope.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"], input[name="id"][form^="product-form-"], form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
      candidates.push(input.value);
    });
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] && variants[String(candidates[i])]) return String(candidates[i]);
    }
    var ids = Object.keys(variants);
    return ids.length === 1 ? ids[0] : '';
  }
  function getMonths(ctx) {
    if (ctx.variant && ctx.variant.months > 0) return ctx.variant.months;
    var r = ctx.scope.querySelector('variant-options input[type="radio"]:checked');
    var m = parseInt(((r && r.value) || '').split('-')[0], 10);
    return m > 0 ? m : 1;
  }
  function getCopy(ctx) {
    var raw = (ctx.variant && ctx.variant.copy) || {};
    return {
      benefitHtml: raw.benefitHtml || '',
      supplyHtml: raw.supplyHtml || '',
      accentHtml: raw.accentHtml || '',
      perdayHtml: raw.perdayHtml || '',
      onetimeHtml: raw.onetimeHtml || ''
    };
  }
  // 'onetime', 'subscription' or '' - from the widget UI first; the hidden selling_plan input
  // is only a fallback, because Appstle may keep its value while One Time Purchase is selected.
  function getPurchaseMode(widget) {
    if (!widget) return '';
    var checked = widget.querySelector('input[type="radio"]:checked');
    if (checked) {
      if (checked.closest(ONE_TIME_SEL) || /one[\s_-]?time/i.test((checked.id || '') + ' ' + (checked.value || ''))) return 'onetime';
    }
    var active = widget.querySelector('.appstle-active-option');
    if (active) {
      if (active.matches(ONE_TIME_SEL) || active.querySelector('.appstle_one_time_description_text')) return 'onetime';
      if (active.querySelector('.appstleSellingPlanDescription')) return 'subscription';
    }
    if (checked) {
      var label = checked.closest('label') || (checked.id && widget.querySelector('label[for="' + checked.id + '"]'));
      var text = (label ? label.textContent : '') || '';
      if (/one\s*time/i.test(text)) return 'onetime';
      if (/subscri/i.test(text)) return 'subscription';
    }
    return '';
  }
  function getSelectedPlanId(ctx) {
    var v = ctx.variant;
    var planIds = v ? Object.keys(v.plans || {}) : [];
    var mode = getPurchaseMode(ctx.widget);
    if (mode === 'onetime') return '';
    var inputs = Array.prototype.slice.call(ctx.scope.querySelectorAll('input[name="selling_plan"]'));
    var input = inputs.filter(function (el) { return !el.disabled && el.value; })[0];
    var fromInput = input ? String(input.value) : '';
    if (mode === 'subscription') {
      if (fromInput && planIds.indexOf(fromInput) !== -1) return fromInput;
      return planIds.length ? planIds[0] : '';
    }
    return (fromInput && planIds.indexOf(fromInput) !== -1) ? fromInput : '';
  }
  function perDayOf(ctx, cents) {
    var days = Math.round(getMonths(ctx) * 30.4);
    return (days > 0 && cents > 0) ? Math.round(cents / days) : 0;
  }

  // ---------- main price ----------
  function perDayMarkup(perday) {
    return perday > 0 ? '<span class="bv-price__perday">' + formatMoney(perday) + '/day</span>' : '';
  }
  function buildPriceMarkup(current, reference, perday) {
    if (reference > current) {
      return '<div class="price price--on-sale">'
        + '<div class="price__container">'
        + '<div class="price__sale visible-main-product">'
        + '<div class="price__sale__details-wrapper">'
        + '<span class="price-item price-item--sale price-item--last wt-product__price__final">' + formatMoney(current) + '</span>'
        + '<span class="price-item--percent">SAVE ' + formatMoney(reference - current) + '</span>'
        + '</div>'
        + '<span><s class="price-item price-item--regular wt-product__price__compare">' + formatMoney(reference) + '</s></span>'
        + perDayMarkup(perday)
        + '</div></div></div>';
    }
    return '<div class="price"><div class="price__container"><div class="price__regular">'
      + '<span class="price-item price-item--regular wt-product__price__final">' + formatMoney(current) + '</span>'
      + perDayMarkup(perday)
      + '</div></div></div>';
  }
  function getPriceBlocks(scope) {
    return Array.prototype.slice.call(
      scope.querySelectorAll('.wt-product__price[id^="price-"], [id^="sticky-price-"]')
    );
  }
  function renderMainPrice(ctx) {
    var v = ctx.variant;
    if (!ctx.data.hasPlans || !v) return;
    var planId = getSelectedPlanId(ctx);
    var onPlan = planId && v.plans[planId] != null;
    var current = onPlan ? v.plans[planId] : v.price;
    var reference = Math.max(v.compareAt || 0, onPlan ? v.price : 0);
    var perday = perDayOf(ctx, current);
    var sig = ctx.variantId + '|' + planId + '|' + current + '|' + reference + '|' + perday;
    getPriceBlocks(ctx.scope).forEach(function (block) {
      var holder = null;
      for (var i = 0; i < block.children.length; i++) {
        if (block.children[i].classList.contains('bv-price')) { holder = block.children[i]; break; }
      }
      if (holder && holder.getAttribute('data-sig') === sig && block.classList.contains('bv-price-ready')) return;
      if (!holder) {
        holder = document.createElement('div');
        holder.className = 'bv-price';
        block.appendChild(holder);
      }
      holder.setAttribute('data-sig', sig);
      holder.innerHTML = buildPriceMarkup(current, reference, perday);
      if (!block.classList.contains('bv-price-ready')) block.classList.add('bv-price-ready');
      block.style.opacity = '1';
    });
  }

  // ---------- Appstle widget ----------
  function buildOnetimeHtml(copy) {
    if (copy.onetimeHtml) return '<div class="appstle-onetime"><div class="appstle-rte">' + copy.onetimeHtml + '</div></div>';
    return DEFAULT_ONETIME_HTML;
  }
  function buildSupplyLine(months) {
    if (months === 1) return '<strong>1-month supply (1 pack)</strong> delivered every month.';
    return '<strong>' + months + '-month supply (' + months + ' packs)</strong> delivered every ' + months + ' months.';
  }
  function buildSubHtml(savePct, perday, months, copy) {
    var supplyHtml = copy.supplyHtml ? '<div class="appstle-rte">' + copy.supplyHtml + '</div>' : buildSupplyLine(months);
    var html = '<ul class="appstle-bullets">'
      + (copy.benefitHtml ? '<li><div class="appstle-rte">' + copy.benefitHtml + '</div></li>' : '')
      + '<li>' + supplyHtml + '</li>';
    if (savePct > 0) {
      html += '<li class="appstle-accent"><strong>Save ' + savePct + '%</strong>'
        + (copy.accentHtml ? ' + <div class="appstle-rte">' + copy.accentHtml + '</div>' : '') + '</li>';
    }
    html += '</ul>';
    if (perday > 0) {
      html += '<div class="appstle-perday">That\'s just <strong>' + formatMoney(perday) + '/day</strong>'
        + (copy.perdayHtml ? ' <div class="appstle-rte">' + copy.perdayHtml + '</div>' : '')
        + '</div>';
    }
    return html;
  }
  // Equal min-height on interval buttons when one of them wraps to 3 lines.
  function fixOptionButtonHeights(widget) {
    var wrappers = Array.prototype.slice.call(widget.querySelectorAll('.appstle_subscription_radio_wrapper'));
    if (!wrappers.length) return;
    wrappers.forEach(function (w) { w.style.minHeight = ''; });
    var needsTall = false;
    var maxH = 0;
    wrappers.forEach(function (w) {
      var lbl = w.querySelector('label');
      if (!lbl) return;
      var s = window.getComputedStyle(lbl);
      var lh = parseFloat(s.lineHeight) || parseFloat(s.fontSize) * 1.4;
      var textH = lbl.scrollHeight - (parseFloat(s.paddingTop) || 0) - (parseFloat(s.paddingBottom) || 0);
      if (lh > 0 && textH / lh > 2.5) needsTall = true;
      if (lbl.scrollHeight > maxH) maxH = lbl.scrollHeight;
    });
    if (needsTall) wrappers.forEach(function (w) { w.style.minHeight = maxH + 'px'; });
  }
  function updateWidget(ctx) {
    var widget = ctx.widget;
    var v = ctx.variant;
    if (!widget) return;
    var subEl = widget.querySelector('.appstle-active-option .appstle_subscription_amount') || widget.querySelector('.appstle_subscription_amount');
    var otEl = widget.querySelector('.appstle-active-option .appstle_subscription_compare_amount') || widget.querySelector('.appstle_subscription_compare_amount');
    var sub = parseCents(subEl && subEl.textContent);
    var ot = parseCents(otEl && otEl.textContent);
    var savePct = (ot > 0 && sub > 0 && ot > sub) ? Math.floor((ot - sub) * 100 / ot) : 0;
    if (v) {
      var pid = getSelectedPlanId(ctx);
      var pIds = Object.keys(v.plans || {});
      var planP = (pid && v.plans[pid] != null) ? v.plans[pid] : (pIds.length ? v.plans[pIds[0]] : 0);
      var ref = Math.max(v.compareAt || 0, v.price);
      if (planP > 0) {
        sub = planP;
        savePct = ref > planP ? Math.floor((ref - planP) * 100 / ref) : 0;
      }
    }
    var months = getMonths(ctx);
    var days = Math.round(months * 30.4);
    var perday = (days > 0 && sub > 0) ? Math.round(sub / days) : 0;
    var copy = getCopy(ctx);

    var subDesc = widget.querySelector('.appstle-active-option .appstleSellingPlanDescription') || widget.querySelector('.appstleSellingPlanDescription');
    if (subDesc) {
      var sig = savePct + '|' + perday + '|' + months + '|' + ctx.variantId
        + '|' + copy.benefitHtml + '|' + copy.supplyHtml + '|' + copy.accentHtml + '|' + copy.perdayHtml;
      if (subDesc.getAttribute('data-wt-sig') !== sig || !subDesc.querySelector('.appstle-bullets')) {
        subDesc.innerHTML = buildSubHtml(savePct, perday, months, copy);
        subDesc.setAttribute('data-wt-sig', sig);
      }
    }
    var otDesc = widget.querySelector('.appstle_one_time_description_text');
    if (otDesc) {
      var otSig = ctx.variantId + '|' + copy.onetimeHtml;
      if (otDesc.getAttribute('data-wt-ot-sig') !== otSig || !otDesc.querySelector('.appstle-onetime')) {
        otDesc.innerHTML = buildOnetimeHtml(copy);
        otDesc.querySelectorAll('.appstle-onetime ul').forEach(function (ul) { ul.classList.add('appstle-bullets'); });
        otDesc.setAttribute('data-wt-ot-sig', otSig);
      }
    }
    // Appstle may set the SAVE badge color inline with !important
    ctx.scope.querySelectorAll('.appstle_subscription_element.appstle_subscription_save').forEach(function (el) {
      el.style.setProperty('border-color', '#CF4A4A', 'important');
      el.style.setProperty('color', '#CF4A4A', 'important');
      el.querySelectorAll('*').forEach(function (child) { child.style.setProperty('color', '#CF4A4A', 'important'); });
    });
    applyWidgetSavings(ctx);
    fixOptionButtonHeights(widget);
  }
  function widgetTextNodes(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var out = [];
    while (walker.nextNode()) {
      if (/\d/.test(walker.currentNode.nodeValue)) out.push(walker.currentNode);
    }
    return out;
  }
  function pillOf(el) {
    var e = el;
    for (var i = 0; i < 3 && e; i++) {
      var bg = window.getComputedStyle(e).backgroundColor;
      if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') return e;
      e = e.parentElement;
    }
    return el;
  }
  function findOneTimeTitle(widget) {
    var all = widget.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var e = all[i];
      if (e.closest('.appstle-bullets, .bv-ot-save, .appstle_one_time_description_text')) continue;
      var own = '';
      for (var j = 0; j < e.childNodes.length; j++) {
        if (e.childNodes[j].nodeType === 3) own += ' ' + e.childNodes[j].nodeValue;
      }
      if (/one\s*time\s*purchase/i.test(own)) return e;
    }
    return null;
  }
  // Subscribe row: strikethrough -> reference price; SAVE badge -> reference - plan price.
  // One Time Purchase row: "SAVE $X" when the variant has a compare-at price.
  // Amounts are matched by value, so this survives Appstle re-renders.
  function applyWidgetSavings(ctx) {
    var v = ctx.variant;
    var widget = ctx.widget;
    if (!v || !widget) return;
    var ref = Math.max(v.compareAt || 0, v.price);
    var planIds = Object.keys(v.plans || {});
    var badgeEl = null;
    widgetTextNodes(widget).forEach(function (node) {
      var el = node.parentElement;
      if (!el || el.closest('.appstle-bullets, .appstle-perday, .bv-ot-save, .appstleSellingPlanDescription, .appstle_one_time_description_text')) return;
      var m = node.nodeValue.match(MONEY_RE);
      if (!m) return;
      var cents = parseCents(m[0]);
      if (el.closest('.appstle_subscription_compare_amount')) {
        if (planIds.length && ref > v.price && cents === v.price) {
          node.nodeValue = node.nodeValue.replace(m[0], formatMoney(ref));
        }
        return;
      }
      var text = el.textContent + ' ' + (el.parentElement ? el.parentElement.textContent : '');
      if (!/save/i.test(text)) return;
      planIds.forEach(function (pid) {
        var p = v.plans[pid];
        var oldSave = v.price - p;
        var newSave = ref - p;
        if (oldSave > 0 && newSave !== oldSave && cents === oldSave) {
          node.nodeValue = node.nodeValue.replace(m[0], formatMoney(newSave));
          badgeEl = el;
        } else if (newSave > 0 && cents === newSave) {
          badgeEl = el;
        }
      });
    });
    var existing = widget.querySelector('.bv-ot-save');
    var otSave = (v.compareAt || 0) > v.price ? v.compareAt - v.price : 0;
    if (!otSave) {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }
    var label = 'SAVE ' + formatMoney(otSave);
    var badge = existing;
    if (!badge) {
      var title = findOneTimeTitle(widget);
      if (!title) return;
      badge = document.createElement('span');
      badge.className = 'bv-ot-save';
      if (title.tagName === 'LABEL') title.appendChild(badge);
      else title.insertAdjacentElement('afterend', badge);
    }
    if (badge.textContent !== label) badge.textContent = label;
    if (badgeEl) syncBadgeStyle(badge, badgeEl);
  }
  // Same size/look as Appstle's SAVE badge (style changes are not observed, so no loop).
  function syncBadgeStyle(badge, textEl) {
    var pill = pillOf(textEl);
    var ts = window.getComputedStyle(textEl);
    var ps = window.getComputedStyle(pill);
    var s = badge.style;
    s.fontFamily = ts.fontFamily;
    s.fontSize = ts.fontSize;
    s.fontWeight = ts.fontWeight;
    s.letterSpacing = ts.letterSpacing;
    s.textTransform = ts.textTransform;
    s.color = ts.color;
    s.lineHeight = ps.lineHeight;
    s.backgroundColor = ps.backgroundColor;
    s.borderRadius = ps.borderRadius;
    s.paddingTop = ps.paddingTop;
    s.paddingBottom = ps.paddingBottom;
    s.paddingLeft = ps.paddingLeft;
    s.paddingRight = ps.paddingRight;
    s.borderWidth = ps.borderTopWidth;
    s.borderStyle = ps.borderTopStyle;
    s.borderColor = ps.borderTopColor;
    s.boxSizing = 'border-box';
    var h = pill.getBoundingClientRect().height;
    if (h > 0) s.height = h + 'px';
  }

  // ---------- hide the price while Appstle repaints after a change ----------
  function endPriceUpdate(block) {
    var state = block && block.__wtPriceUpdate;
    if (!state) return;
    clearTimeout(state.settleTimer);
    clearTimeout(state.fallbackTimer);
    if (state.observer) state.observer.disconnect();
    block.__wtPriceUpdate = null;
    var priceEl = block.querySelector('.price');
    if (priceEl) priceEl.classList.remove('wt-price-updating');
  }
  function beginPriceUpdate(block) {
    var priceEl = block && block.querySelector('.price');
    if (!priceEl) return;
    var state = block.__wtPriceUpdate;
    if (!state) {
      state = { observer: null, settleTimer: null, fallbackTimer: null };
      block.__wtPriceUpdate = state;
      state.observer = new MutationObserver(function () {
        clearTimeout(state.settleTimer);
        state.settleTimer = setTimeout(function () { endPriceUpdate(block); }, PRICE_SETTLE_MS);
      });
      state.observer.observe(block, { childList: true, subtree: true, characterData: true });
    }
    priceEl.classList.add('wt-price-updating');
    clearTimeout(state.settleTimer);
    state.settleTimer = null;
    clearTimeout(state.fallbackTimer);
    state.fallbackTimer = setTimeout(function () { endPriceUpdate(block); }, PRICE_FALLBACK_MS);
  }

  // ---------- run ----------
  function processContext(ctx) {
    updateWidget(ctx);
    renderMainPrice(ctx);
  }
  function processScope(scope) {
    var ctx = getContext(scope);
    if (ctx) processContext(ctx);
  }
  function processAll() {
    allContexts().forEach(processContext);
  }

  var pending = null; // array of scopes, or 'all'
  function schedule(scope) {
    if (pending === 'all') return;
    if (!scope || scope === document) {
      var wasIdle = pending === null;
      pending = 'all';
      if (wasIdle) raf(flush);
      return;
    }
    if (pending === null) {
      pending = [scope];
      raf(flush);
    } else if (pending.indexOf(scope) === -1) {
      pending.push(scope);
    }
  }
  function flush() {
    var work = pending;
    pending = null;
    if (work === 'all') processAll();
    else if (work) work.forEach(processScope);
  }
  function scopeForNode(node) {
    var el = node && (node.nodeType === 1 ? node : node.parentElement);
    if (!el) return null;
    var scope = scopeOf(el);
    if (scope) return scope;
    // nodes outside any section (e.g. the quick-add wrapper itself being filled)
    if (el.querySelector && el.querySelector('script[data-bv-product]')) return document;
    return null;
  }

  function start() {
    if (document.__bvPricingStarted) { schedule(document); return; }
    document.__bvPricingStarted = true;
    processAll();

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var scope = scopeForNode(records[i].target);
        if (!scope) continue;
        if (scope !== document && !scope.querySelector('script[data-bv-product]')) continue;
        schedule(scope);
        if (pending === 'all') break;
      }
    }).observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'value', 'checked', 'aria-checked', 'data-variant-id']
    });

    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      if (!target.closest('variant-options') && !target.closest('.appstle_sub_widget')) return;
      var scope = scopeOf(target);
      if (!scope) return;
      getPriceBlocks(scope).forEach(beginPriceUpdate);
      setTimeout(function () { processScope(scope); }, 60);
      setTimeout(function () { processScope(scope); }, 300);
    });

    // Safety: never leave a price hidden
    setTimeout(function () {
      document.querySelectorAll('[id^="price-"]').forEach(function (el) { el.style.opacity = '1'; });
    }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.addEventListener('load', function () { schedule(document); });
  window.addEventListener('pageshow', function () { schedule(document); });

  window.BIGVITA = window.BIGVITA || {};
  window.BIGVITA.refreshPricing = function () { schedule(document); };
})();
